import React, { useContext, useEffect, useRef, useState } from 'react'
// // material
import { makeStyles } from '@material-ui/core/styles'
import { Autocomplete, Box, Button, Grid, TextField, Typography } from '@mui/material'
import { ContractType, LegalEntityParty, Terms } from '@types'
import {
  commonNoActionButtonStyle,
  commonProceedButtonStyle
} from 'components/request-action/styles/styles'
import { GlobalInfoContext, initialState } from 'contexts/GlobalStateContext'
import { CreateGxInitialValues } from 'contexts/infer-types/create-initial'
import { Dayjs } from 'dayjs'
import { useFormik } from 'formik'
import { useSocketConnection } from 'hooks/useSocketConnection'
import { base64ToPdf, sendPDFCall } from 'pages/dashboard/utils'
import { useNavigate } from 'react-router-dom'
import { startLoadingPDF } from 'redux/slices/pdf'
import { PATH_DASHBOARD } from 'routes/paths'
import * as Yup from 'yup'
import { getMinExpiryDays, getExpiryDateErrorMessage } from 'utils/expiryDateConfig'
import { RootState, dispatch, useSelector } from '../../../../redux/store'
import { PRIMARY } from '../../../../theme/palette'
import { AmountAndCurrency } from '../../sections/AmountAndCurrency'
import { EntityDetailsRow } from '../../sections/EntityRow'
import { TermsAndConditionsSelection } from '../../sections/autocomplete/DropDowns'
import { ExpiryDate } from '../../sections/section'
import { ElementBreak, Heading, SectionBreak } from './shared'
import { convertAmountStringToObject, defineAmountState } from './utils'
import { sanitizeInput } from 'components/request-action/sanitzeUnicode'
import { PointOfContact } from './types/PointOfContact'
import { AU_STATES } from '../../../../constants'
import {
  trimWhitespace,
  collapseWhitespace,
  replaceUnicodeWhitespaceWithSpace,
  convertLiteralUnicodeToActual
} from 'utils/whitespaceUtils'

const illegalCharacterMessage = 'Unsupported character replaced with acceptable equivalent'
const useStyles = makeStyles({
  root: {
    display: 'flex',
    width: '100%',
    paddingBottom: '2rem',
    justifyContent: 'space-between',
    textAlign: 'right',
    fontFamily: 'Inter',
    fontWeight: 700,
    fontSize: 20
  }
})

export type InitialValues = CreateGxInitialValues & {
  propertyName: string
  street: string
  suburb: string
  postcode: string
  state: string
  country: string
}

const CommercialLease = () => {
  const classes = useStyles()
  const { onSocketSend } = useSocketConnection()
  const navigate = useNavigate()
  const [, setApplicant] = useState<LegalEntityParty>({
    commonName: '',
    legalEntityIdentifier: '',
    legalEntityCodes: [
      {
        legalEntityCodeType: 'ABN',
        legalEntityCodeData: ''
      }
    ]
  })
  const [, setBene] = useState<LegalEntityParty>({
    commonName: '',
    legalEntityIdentifier: '',
    legalEntityCodes: [
      {
        legalEntityCodeType: 'ABN',
        legalEntityCodeData: ''
      }
    ]
  })
  const [, setIssuer] = useState<LegalEntityParty>({
    commonName: '',
    legalEntityIdentifier: '',
    legalEntityCodes: [
      {
        legalEntityCodeType: 'ABN',
        legalEntityCodeData: ''
      }
    ]
  })

  const [, setCurrency] = useState('AUD')

  const [, setstartDateValue] = React.useState<Dayjs | null>(null)
  const [, setendDateValue] = React.useState<Dayjs | null>(null)
  const [contractExpiryValue, setContractExpiryValue] = React.useState<Dayjs | null>(null)
  const [isTermsClicked, setIsTermsClicked] = useState(false)
  const [illegalCharInPurposeDescError, setIllegalCharInPurposeDescError] = useState(false)
  const [illegalCharInOptionalCommentsError, setIllegalCharInOptionalCommentsError] =
    useState(false)
  const [illegalCharSuburbError, setIllegalCharSuburbError] = useState(false)
  const [illegalCharPropertyNameError, setIllegalCharPropertyError] = useState(false)
  const [illegalCharStreetAddError, setIllegalCharStreetAddError] = useState(false)
  const [illegalCharInContractNumberError, setIllegalCharContractNumberError] = useState(false)
  const [illegalCharInReference, setIllegalCharInReference] = useState(false)
  const [isOnboarded, setIsOnboarded] = useState<boolean>(true)
  const { isLoading: isTermsPdfLoading } = useSelector((state: RootState) => state.pdfReducer)

  const {
    setReqInfo,
    purpose,
    applicants,
    beneficiaries,
    issuers,
    reference,
    isOpenEnded,
    type,
    id,
    expiryDate,
    amount: contextAmount,
    terms: contextTerms
  } = useContext(GlobalInfoContext)
  const initiatedBy = location.pathname.includes('applicant') ? 'applicant' : 'beneficiary'
  const issuerRef = useRef()
  const [amountSet, setAmount] = useState(defineAmountState(contextAmount))
  const [terms, setTerms] = useState<Terms>(contextTerms)

  const [, setStartYear] = useState('')
  const [, setStartMonth] = useState('')
  const [, setStartDay] = useState('')
  const [, setContractExpiryYear] = useState('')
  const [, setContractExpiryMonth] = useState('')
  const [, setContractExpiryDay] = useState('')
  const [, setCountry] = useState<string>('')

  const [, setendDateYear] = useState('')
  const [, setendDateMonth] = useState('')
  const [, setendDateDay] = useState('')
  const [isTermsSelected, setIsTermsSelected] = useState(false)

  const { businessUnitActionContexts: buacs } = useSelector(
    (state: RootState) => state.createGuaranteePayload
  )
  const digitalBuacs = buacs.filter((b) => b.contractType === ContractType.TriContract)
  const { issuers: issuersInReal } = useSelector((root: RootState) => root.termsReducer)
  const pointOfContactSchema = Yup.object().shape({
    position: Yup.string().notRequired(),
    person: Yup.object().shape({
      firstName: Yup.string().required('First name is required'),
      lastName: Yup.string().required('Last name is required'),
      phone: Yup.string().required('Phone is required'),
      email: Yup.string().email('Invalid email').required('Email is required')
    })
  })

  const CommercialGuaranteeCreateSchema = Yup.object()
    .shape({
      applicants: Yup.string().required('Applicant name is required'),

      beneficiaries: Yup.array()
        .of(
          Yup.object().shape({
            commonName: Yup.string().required('Beneficiary name is required'),
            legalEntityIdentifier: Yup.string().required(
              'Please enter a valid beneficiary ABN/ACN'
            ),
            onboarded: Yup.boolean().notRequired(),
            designatedPointOfContact: Yup.mixed().when('onboarded', {
              is: false,
              then: pointOfContactSchema.required('Point of contact is required'),
              otherwise: Yup.mixed().nullable(true)
            })
          })
        )
        .min(1, 'At least one Beneficiary is required'),

      issuers: Yup.string().when([], {
        is: () => initiatedBy === 'beneficiary',
        then: Yup.string().notRequired(),
        otherwise: Yup.string().required('Issuer Name is required')
      }),

      amount: Yup.string().min(1).required('Amount cannot be empty'),

      number: Yup.string().notRequired(),
      propertyName: Yup.string().notRequired(),
      street: Yup.string().notRequired(),
      suburb: Yup.string().notRequired(),

      postcode: Yup.string()
        .min(4)
        .notRequired()
        .test('specialChars', 'Postcode format not allowed', (value) =>
          /^[a-z0-9-]+$/i.test(String(value))
        ),

      purposeDescription: Yup.string()
        .trim()
        .min(3)
        .required('Purpose of the guarantee is required'),

      state: Yup.string().notRequired(),

      terms: Yup.string().required('Please select terms'),

      isOpenEnded: Yup.boolean().notRequired(),

      expiryDate: Yup.date().when('isOpenEnded', {
        is: false,
        then: Yup.date()
          .required('Expiry Date is required')
          .test('min-expiry-days', getExpiryDateErrorMessage(), function (value) {
            if (!value) return true
            const today = new Date()
            today.setHours(0, 0, 0, 0)
            const minDate = new Date(today)
            minDate.setDate(minDate.getDate() + getMinExpiryDays())
            return value >= minDate
          }),
        otherwise: Yup.date().notRequired()
      }),

      reference: Yup.string().notRequired()
    })
    .test(
      'applicant-beneficiary-not-same',
      'Applicant and Beneficiary cannot be the same legal entity',
      function (values) {
        const applicant = values?.applicants?.toLowerCase?.().trim?.()
        const beneficiary = values?.beneficiaries?.[0]?.commonName?.toLowerCase?.().trim?.()

        if (applicant && beneficiary && applicant === beneficiary) {
          return this.createError({
            path: 'beneficiaries',
            message: 'Applicant and Beneficiary cannot be the same legal entity'
          })
        }

        return true
      }
    )
    .test('bene-issuer-test', 'Beneficiary and Issuer cannot be the same', function (values) {
      const issuer = values?.issuers?.toLowerCase()?.trim?.()
      const beneficiary = values?.beneficiaries?.[0]?.commonName?.toLowerCase()?.trim?.()

      if (issuer && beneficiary && issuer === beneficiary) {
        return this.createError({
          path: 'issuers',
          message: 'Beneficiary and Issuer cannot be the same'
        })
      }

      return true
    })

  const formik = useFormik<InitialValues>({
    initialValues: {
      applicants: applicants[0]?.commonName || '',
      beneficiaries: [
        {
          legalEntityIdentifier: beneficiaries?.[0]?.legalEntityIdentifier || '',
          // prefer businessIDcomplete or ABN/ACN/identifier when commonName not provided
          commonName:
            beneficiaries?.[0]?.commonName ||
            (beneficiaries?.[0] as any)?.businessIDcomplete ||
            beneficiaries?.[0]?.legalEntityCodes?.[0]?.legalEntityCodeData ||
            beneficiaries?.[0]?.legalEntityIdentifier ||
            '',
          onboarded: beneficiaries[0]?.onboarded ?? false,
          designatedPointOfContact: beneficiaries?.[0]?.designatedPointOfContact
            ? {
                position: beneficiaries?.[0]?.designatedPointOfContact?.position || '',
                person: {
                  firstName: beneficiaries?.[0]?.designatedPointOfContact?.person?.firstName || '',
                  lastName: beneficiaries?.[0]?.designatedPointOfContact?.person?.lastName || '',
                  phone: beneficiaries?.[0]?.designatedPointOfContact?.person?.phone || '',
                  email: beneficiaries?.[0]?.designatedPointOfContact?.person?.email || ''
                }
              }
            : undefined,
          requestedTrustee: beneficiaries?.[0]?.requestedTrustee,
          legalEntityType: beneficiaries?.[0]?.legalEntityType || '',
          handlingInstructions: beneficiaries?.[0]?.handlingInstructions
        }
      ],
      issuers: issuers[0]?.commonName || '',
      amount: defineAmountState(contextAmount),
      propertyName: !id ? '' : purpose?.property?.name || '',
      number: !id ? '' : purpose?.contract?.number || '',
      //unit: purpose?.property?.unit || '',
      terms: terms.termsDisplay || '',
      street: !id ? '' : purpose?.property?.street || '',
      suburb: !id ? '' : purpose?.property?.suburb || '',
      postcode: !id ? '' : purpose?.property?.postcode || '',
      state: !id ? '' : purpose?.property?.state || '',
      country: !id ? 'Australia' : purpose?.property?.country || 'Australia',
      purposeDescription: !id ? '' : purpose?.purposeDescription || '',
      expiryDate: expiryDate || '',
      isOpenEnded: isOpenEnded || false,
      reference: reference || '',
      name: ''
    },
    validationSchema: CommercialGuaranteeCreateSchema,
    onSubmit: (values) => {
      setReqInfo({
        reference: values?.reference,
        terms,
        amount: convertAmountStringToObject(amountSet),
        expiryDate: values?.expiryDate || '',
        isOpenEnded: values?.isOpenEnded,
        purpose: {
          ...purpose,
          purposeDescription: values?.purposeDescription,
          property: {
            ...purpose?.property,
            name: values?.propertyName || '',
            //unit: values?.unit || '',
            street: values?.street || '',
            suburb: values?.suburb || '',
            postcode: values?.postcode || '',
            state: values?.state || '',
            country: values?.country || ''
          },
          contract: {
            number: values?.number
          }
        }
      })
      navigate(PATH_DASHBOARD.general.guaranteeCreate.review)
    }
  })
  const { errors, touched, getFieldProps } = formik
  const previousValuesRef = useRef<string | null>(null)

  // Persist form data to context/localStorage whenever values change
  useEffect(() => {
    // Don't save on initial load, only on changes after user interaction
    if (
      (Object.keys(formik.touched).length > 0 || formik.dirty) &&
      previousValuesRef.current !== JSON.stringify(formik.values)
    ) {
      previousValuesRef.current = JSON.stringify(formik.values)

      setReqInfo({
        applicants:
          applicants && applicants.length > 0
            ? applicants
            : [{ commonName: formik.values.applicants }],
        beneficiaries:
          beneficiaries && beneficiaries.length > 0 ? beneficiaries : formik.values.beneficiaries,
        issuers: issuers && issuers.length > 0 ? issuers : [{ commonName: formik.values.issuers }],
        amount: convertAmountStringToObject(amountSet),
        reference: formik.values.reference,
        expiryDate: formik.values.expiryDate || '',
        isOpenEnded: formik.values.isOpenEnded,
        purpose: {
          ...purpose,
          purposeDescription: formik.values.purposeDescription,
          property: {
            ...purpose?.property,
            name: formik.values.propertyName || '',
            street: formik.values.street || '',
            suburb: formik.values.suburb || '',
            postcode: formik.values.postcode || '',
            state: formik.values.state || '',
            country: formik.values.country || 'Australia'
          },
          contract: {
            number: formik.values.number,
          }
        },
        terms
      })
      // Set isTermsSelected when terms are present
      if (terms?.termsDisplay) {
        setIsTermsSelected(true)
      }
    }
  }, [setReqInfo, terms, amountSet, applicants, beneficiaries, issuers, purpose])

  const handleOpenEndedDate = (value) => {
    setReqInfo({
      isOpenEnded: !isOpenEnded,
      expiryDate: null,
      ...purpose
    })
    value && formik.setFieldValue('expiryDate', '')
    setContractExpiryValue(null)
    formik.setFieldValue('isOpenEnded', value)
  }

  const handleTerms = (value: Terms) => {
    formik.setFieldValue('terms', value.termsDisplay, true)
    setTerms(value)
    setReqInfo({
      terms: value
    })
  }
  // Usage
  async function executePreviewTerms(documentIdentifier: string, roleIdentifier: string) {
    dispatch(startLoadingPDF())
    await sendPDFCall(documentIdentifier, roleIdentifier, onSocketSend)
  }

  const sanitizeComment = (input, type) => {
    let processedInput = input

    // For reference (IRN) field, apply Unicode whitespace sanitization
    if (type === 'reference') {
      // Convert literal Unicode escape sequences to actual characters
      processedInput = convertLiteralUnicodeToActual(processedInput)
      // Replace all Unicode whitespaces with ASCII space
      processedInput = replaceUnicodeWhitespaceWithSpace(processedInput)
      // Collapse consecutive spaces
      processedInput = collapseWhitespace(processedInput)
      // Trim leading/trailing whitespace
      processedInput = trimWhitespace(processedInput)
    }

    const { sanitized, mapped, ignored } = sanitizeInput(processedInput)

    const hasIssue = mapped || ignored

    switch (type) {
      case 'purposeDescription':
        setIllegalCharInPurposeDescError(hasIssue)
        break
      case 'comments':
        setIllegalCharInOptionalCommentsError(hasIssue)
        break
      case 'number':
        setIllegalCharContractNumberError(hasIssue)
        break
      case 'suburb':
        setIllegalCharSuburbError(hasIssue)
        break
      case 'propertyName':
        setIllegalCharPropertyError(hasIssue)
        break
      case 'street':
        setIllegalCharStreetAddError(hasIssue)
        break
      case 'reference':
        setIllegalCharInReference(hasIssue)
        break
      default:
        // Optionally handle unknown types
        break
    }

    return sanitized
  }
  useEffect(() => {
    isTermsClicked && !isTermsPdfLoading && base64ToPdf('terms.pdf', 'pdf')
  }, [isTermsPdfLoading, isTermsClicked])

  const handleEntityValue = (value: LegalEntityParty, type: string) => {
    if (type === 'applicants') {
      formik.setFieldValue('applicants', value?.commonName, true) // wrap in array ✅
      setApplicant(value)
      setReqInfo({
        applicants: [value]
      })
    } else if (type === 'beneficiaries') {
      const beneficiaryEntry = {
        ...value,
        // ensure commonName contains business ID when commonName absent
        commonName:
          value?.commonName ||
          (value as any)?.businessIDcomplete ||
          value?.legalEntityCodes?.[0]?.legalEntityCodeData ||
          value?.legalEntityIdentifier ||
          '',
        onboarded: value?.onboarded ?? false,
        // Preserve existing designatedPointOfContact if present, otherwise use value's or undefined
        designatedPointOfContact: value?.designatedPointOfContact || beneficiaries?.[0]?.designatedPointOfContact
      }
      formik.setFieldValue('beneficiaries', [beneficiaryEntry], true) // wrap in array ✅
      setBene(value)
      setReqInfo({
        beneficiaries: [beneficiaryEntry]
      })
    } else if (type === 'issuers') {
      formik.setFieldValue('issuers', value?.commonName, true)
      setIssuer(value)
      setReqInfo({ issuers: [value] })
    }
  }

  const handlePointOfContact = (poc: PointOfContact, type: string) => {
    if (type === 'beneficiaries') {
      const current = formik.values.beneficiaries?.[0] ?? { legalEntityIdentifier: '' }
      const updated = {
        ...current,
        designatedPointOfContact: poc
      }
      formik.setFieldValue('beneficiaries', [updated], true)
      setReqInfo({ beneficiaries: [updated] })
    }
  }
  const exitCreationProcess = () => {
    navigate(PATH_DASHBOARD.general.app)
    setReqInfo(initialState)
  }

  const handleClear = (type: string) => {
    if (type === 'applicants') {
      formik.setFieldValue('applicants', '', true)
      setReqInfo({
        applicants: []
      })
    } else if (type === 'beneficiaries') {
      formik.setFieldValue('beneficiaries', '', true)
      setReqInfo({
        beneficiary: []
      })
    } else if (type === 'issuers') {
      formik.setFieldValue('issuers', '', true)

      setReqInfo({
        issuers: []
      })
    }
  }
  const childRef = useRef()
  const childRefForTerms = useRef()

  const handleAmount = (value: string) => {
    formik.setFieldValue('amount', value, true)
    setAmount(value)
  }

  const handleDate = (value: Dayjs, type: string) => {
    const formattedValue = value?.format('YYYY/MM/DD')
    if (type === 'expiryDate') {
      formik.setFieldValue('expiryDate', formattedValue, true)
      setContractExpiryValue(value)
      setContractExpiryYear(formattedValue.split('/')[0])
      setContractExpiryMonth(formattedValue.split('/')[1])
      setContractExpiryDay(formattedValue.split('/')[2])
    } else if (type === 'startDate') {
      formik.setFieldValue('startDate', formattedValue, true)

      setstartDateValue(value)
      setStartYear(formattedValue.split('/')[0])
      setStartMonth(formattedValue.split('/')[1])
      setStartDay(formattedValue.split('/')[2])
    } else if (type === 'endDate') {
      formik.setFieldValue('endDate', formattedValue, true)

      setendDateValue(value)
      setendDateYear(formattedValue.split('/')[0])
      setendDateMonth(formattedValue.split('/')[1])
      setendDateDay(formattedValue.split('/')[2])
    }
    if (type === 'expiryDate') {
      setReqInfo({
        purpose: {
          ...purpose,
          expiryDate: formattedValue,
          contract: {
            ...purpose?.contract
          }
        }
      })
    } else {
      setReqInfo({
        purpose: {
          ...purpose,
          contract: {
            ...purpose?.contract,
            [`${type}`]: formattedValue
          }
        }
      })
    }
  }

  const handleReviewAndSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const errors = await formik.validateForm()

    if (Object.keys(errors).length === 0) {
      formik.handleSubmit()
    } else {
      // Handle nested beneficiaries touched state
      const touchedFields: any = {}

      Object.keys(errors).forEach((key) => {
        if (key === 'beneficiaries' && Array.isArray(errors.beneficiaries)) {
          touchedFields.beneficiaries = errors.beneficiaries.map((err) => {
            if (typeof err === 'object' && err !== null) {
              return Object.fromEntries(Object.keys(err).map((k) => [k, true]))
            }
            return true
          })
        } else {
          touchedFields[key] = true
        }
      })

      formik.setTouched(touchedFields, true)

      const firstErrorField = Object.keys(errors)[0]
      const el = document.querySelector(`[name="${firstErrorField}"]`)
      if (el && 'scrollIntoView' in el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }
  }

  const beneficiaryErrors = errors?.beneficiaries?.[0] || {}
  const beneficiaryTouched = touched?.beneficiaries?.[0] || {}
  return (
    <div>
      <form onSubmit={handleReviewAndSubmit}>
        <SectionBreak>
          <ElementBreak>
            <Heading>Parties</Heading>
          </ElementBreak>
          {initiatedBy === 'applicant' ? (
            <div>
              <ElementBreak>
                <ElementBreak>
                  <EntityDetailsRow
                    label="Applicant Name"
                    type="applicants"
                    touched={touched}
                    errors={errors}
                    getFieldProps={getFieldProps}
                    setValue={(value) => handleEntityValue(value, 'applicants')}
                    initialOption={applicants?.[0] ?? undefined}
                    singleEntry
                    handleClear={handleClear}
                    initiatedBy={initiatedBy}
                    initialValues={digitalBuacs}
                    ref={childRef}
                    resetIssuer={issuerRef}
                  />
                </ElementBreak>
              </ElementBreak>
              <ElementBreak>
                <EntityDetailsRow
                  label="Beneficiary Name"
                  type="beneficiaries"
                  touched={beneficiaryTouched}
                  errors={beneficiaryErrors}
                  key={`${type}` ? `bene-${type}` : 'bene-section '}
                  getFieldProps={getFieldProps}
                  setValue={(value) => handleEntityValue(value, 'beneficiaries')}
                  initialOption={beneficiaries[0]}
                  handleClear={handleClear}
                  initiatedBy={initiatedBy}
                  initialValues={[]}
                  resetTermsRef={childRefForTerms}
                  ref={childRef}
                  resetIssuer={issuerRef}
                  setPOC={(value) => value && handlePointOfContact(value, 'beneficiaries')}
                  isOnbordedRequired={beneficiaries?.[0]?.onboarded}
                />
              </ElementBreak>
              <ElementBreak>
                {typeof formik.errors.beneficiaries === 'string' && (
                  <Typography color="error" sx={{ mt: 1 }}>
                    {formik.errors.beneficiaries}
                  </Typography>
                )}
              </ElementBreak>
            </div>
          ) : (
            <div>
              <ElementBreak>
                <EntityDetailsRow
                  label="Beneficiary Name"
                  type="beneficiaries"
                  touched={touched}
                  errors={errors}
                  key={`bene-${type}`}
                  getFieldProps={getFieldProps}
                  setValue={(value) => handleEntityValue(value, 'beneficiaries')}
                  handleClear={handleClear}
                  initiatedBy={initiatedBy}
                  initialValues={digitalBuacs}
                  clearable={false}
                />
              </ElementBreak>
              <ElementBreak>
                <EntityDetailsRow
                  label="Applicant Name"
                  type="applicants"
                  touched={touched}
                  errors={errors}
                  key={`applicant-${type}`}
                  getFieldProps={getFieldProps}
                  setValue={(value) => handleEntityValue(value, 'applicants')}
                  handleClear={handleClear}
                  initialOption={applicants[0]}
                  singleEntry={applicants.length === 1}
                  initiatedBy={initiatedBy}
                  initialValues={digitalBuacs}
                />
              </ElementBreak>
            </div>
          )}
          {initiatedBy === 'applicant' && (
            <>
              <ElementBreak>
                <EntityDetailsRow
                  label="Issuer Name"
                  type="issuers"
                  touched={touched}
                  errors={errors}
                  getFieldProps={getFieldProps}
                  key={`${type}` ? `issuer-${type}` : 'issuer-section '}
                  setValue={(value) => handleEntityValue(value, 'issuers')}
                  initialOption={issuers[0]}
                  handleClear={handleClear}
                  singleEntry={issuersInReal?.[0]?.issuerLegalEntity.length === 1}
                  initialValues={issuersInReal}
                  initiatedBy={initiatedBy}
                  resetTermsRef={childRefForTerms}
                  issuerRef={issuerRef}
                  ref={childRef}
                  clearable={false}
                />
              </ElementBreak>
            </>
          )}
        </SectionBreak>
        <SectionBreak>
          <Box sx={{ flexGrow: 1, ...classes }}>
            <Grid container sx={{ marginTop: '2rem' }}>
              <Heading>Commercial Lease Guarantee Purpose</Heading>
            </Grid>
          </Box>
          <Box>
            <ElementBreak>
              <Grid item md={9} lg={9}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  label="Property Name"
                  sx={{ marginTop: '2rem', width: '99.5%' }}
                  placeholder="Enter Property Name"
                  id="propertyName"
                  name="propertyName"
                  autoComplete="off"
                  onChange={(e) => {
                    const cleaned = sanitizeComment(e.target.value, 'propertyName')
                    if (formik.values.propertyName !== cleaned) {
                      formik.setFieldValue('propertyName', cleaned)
                    }
                  }}
                  value={formik.values.propertyName}
                  error={
                    Boolean(touched.propertyName && errors.propertyName) ||
                    illegalCharPropertyNameError
                  }
                  helperText={
                    touched.propertyName && errors.propertyName
                      ? errors.propertyName
                      : illegalCharPropertyNameError
                      ? illegalCharacterMessage
                      : ''
                  }
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>
            </ElementBreak>
            <ElementBreak>
              <Grid container spacing={2}>
                <Grid item md={9} lg={9}>
                  <TextField
                    InputLabelProps={{ shrink: true }}
                    label="Street Address Including Building, Suite, Floor, Unit etc"
                    name="street"
                    id="street"
                    sx={{ width: '100%' }}
                    placeholder="Enter Street Address"
                    autoComplete="off"
                    value={formik.values.street}
                    onChange={(e) => {
                      const cleaned = sanitizeComment(e.target.value, 'street')
                      if (formik.values.street !== cleaned) {
                        formik.setFieldValue('street', cleaned)
                      }
                    }}
                    error={Boolean(touched.street && errors.street) || illegalCharStreetAddError}
                    helperText={
                      touched.street && errors.street
                        ? errors.street
                        : illegalCharStreetAddError
                        ? illegalCharacterMessage
                        : ''
                    }
                    inputProps={{ maxLength: 100 }}
                  />
                </Grid>
              </Grid>
            </ElementBreak>
            <ElementBreak>
              <Grid container spacing={2}>
                <Grid item md={4} lg={4}>
                  <TextField
                    InputLabelProps={{ shrink: true }}
                    label="Suburb"
                    name="suburb"
                    id="suburb"
                    sx={{ width: '100%' }}
                    placeholder="Enter Suburb"
                    autoComplete="off"
                    value={formik.values.suburb}
                    //onChange={formik.handleChange('suburb')}
                    onChange={(e) => {
                      const cleaned = sanitizeComment(e.target.value, 'suburb')
                      if (formik.values.suburb !== cleaned) {
                        formik.setFieldValue('suburb', cleaned)
                      }
                    }}
                    error={Boolean((touched.suburb && errors.suburb) || illegalCharSuburbError)}
                    helperText={
                      touched.suburb && errors.suburb
                        ? errors.suburb
                        : illegalCharSuburbError
                        ? illegalCharacterMessage
                        : ''
                    }
                    inputProps={{ maxLength: 100 }}
                  />
                </Grid>
                <Grid item md={3} lg={3}>
                  <TextField
                    InputLabelProps={{ shrink: true }}
                    label="Postcode"
                    name="postcode"
                    id="postcode"
                    sx={{ width: '100%' }}
                    placeholder="Enter Postcode"
                    autoComplete="off"
                    value={formik.values.postcode}
                    //onChange={formik.handleChange('postcode')}
                    onChange={(e) => {
                      const cleaned = sanitizeComment(e.target.value, 'postcode')
                      if (formik.values.postcode !== cleaned) {
                        formik.setFieldValue('postcode', cleaned)
                      }
                    }}
                    error={Boolean(touched.postcode && errors.postcode)}
                    helperText={touched.postcode && errors.postcode}
                    inputProps={{
                      maxLength: 15
                    }}
                  />
                </Grid>
                <Grid item md={2} lg={2}>
                  <Autocomplete
                    options={AU_STATES}
                    freeSolo={false}
                    value={formik.values.state}
                    disableClearable={true}
                    getOptionLabel={(option) => (option ? option : '')}
                    onChange={(_, value) => formik.setFieldValue('state', value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="State"
                        id={''}
                        sx={{ width: '100%' }}
                        placeholder="Select State..."
                        {...getFieldProps('state')}
                        error={Boolean(touched['state'] && errors['state'])}
                        helperText={touched['state'] && errors['state']}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                          ...params.inputProps,
                          readOnly: true
                        }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </ElementBreak>
            <ElementBreak>
              <Grid item md={4} lg={4}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  label="Contract Number"
                  placeholder="Enter Contract Number"
                  id="number"
                  name="number"
                  sx={{ width: '97%' }}
                  autoComplete="off"
                  //onChange={formik.handleChange('number')}
                  onChange={(e) => {
                    const cleaned = sanitizeComment(e.target.value, 'number')
                    if (formik.values.number !== cleaned) {
                      formik.setFieldValue('number', cleaned)
                    }
                  }}
                  value={formik.values.number}
                  error={
                    Boolean(touched.number && errors.number) || illegalCharInContractNumberError
                  }
                  helperText={
                    touched.number && errors.number
                      ? errors.number
                      : illegalCharInContractNumberError
                      ? illegalCharacterMessage
                      : ''
                  }
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>
              <Grid item md={1} lg={1} />
            </ElementBreak>
          </Box>

          <ElementBreak>
            <Grid item md={9} lg={9}>
              <TextField
                id="purpose-of-the-guarantee"
                multiline
                rows={10}
                placeholder="The detailed description of the contract (Mandatory)"
                label="Purpose of the Guarantee (Mandatory)"
                autoComplete="off"
                value={formik.values.purposeDescription}
                onChange={(e) => {
                  const cleaned = sanitizeComment(e.target.value, 'purposeDescription')
                  if (formik.values.purposeDescription !== cleaned) {
                    formik.setFieldValue('purposeDescription', cleaned)
                  }
                }}
                size="medium"
                style={{ width: '100%' }}
                InputLabelProps={{ shrink: true }}
                error={
                  Boolean(touched.purposeDescription && errors.purposeDescription) ||
                  illegalCharInPurposeDescError
                }
                helperText={
                  touched.purposeDescription && errors.purposeDescription
                    ? errors.purposeDescription
                    : illegalCharInPurposeDescError
                    ? illegalCharacterMessage
                    : ''
                }
                inputProps={{ maxLength: 500 }}
              />

              {/* ✅ Remaining characters counter */}
              <Box display="flex" justifyContent="flex-end" mt={1}>
                <span style={{ fontSize: '12px', color: '#666' }}>
                  {500 - formik.values.purposeDescription?.length || 0} characters remaining
                </span>
              </Box>
            </Grid>
          </ElementBreak>
        </SectionBreak>
        <Box sx={{ flexGrow: 1, ...classes }}>
          <ElementBreak>
            <Heading>Guarantee Details</Heading>
          </ElementBreak>
        </Box>
        <Box>
          <ElementBreak>
            <ExpiryDate
              expiryDateValue={contractExpiryValue}
              setExpiryDate={handleDate}
              openEnded={formik.values.isOpenEnded}
              handleOpenEndedDate={handleOpenEndedDate}
              errors={errors}
              touched={touched}
            />
          </ElementBreak>
        </Box>
        <Box>
          <ElementBreak>
            <AmountAndCurrency
              setCurrency={setCurrency}
              amount={amountSet}
              setAmount={handleAmount}
              touched={touched}
              errors={errors}
            />
          </ElementBreak>
        </Box>
        <Box>
          <ElementBreak>
            <Grid item md={4} lg={4}>
              <TermsAndConditionsSelection
                setIsTermsSelected={setIsTermsSelected}
                handleEntityValue={handleTerms}
                ref={childRefForTerms}
                errors={errors}
                touched={touched}
              />
            </Grid>
            <Grid item md={1} lg={1} />
            <Grid item md={2} lg={2} sx={{ marginTop: '10px' }}>
              {isTermsSelected && (
                <a
                  href="#"
                  style={{
                    color: PRIMARY.main,
                    marginTop: '10px'
                  }}
                  onClick={() => {
                    setIsTermsClicked(true)
                    executePreviewTerms(
                      terms?.termsIdentifier ?? '',
                      buacs[0]?.businessUnitActionContext[0]?.workflowRole?.roleIdentifier || null
                    )
                  }}
                >
                  Preview Terms
                </a>
              )}
            </Grid>
          </ElementBreak>
        </Box>

        <SectionBreak>
          <Grid item md={4} lg={4}>
            <ElementBreak>
              <Heading>Internal Reference Number</Heading>
            </ElementBreak>
            <TextField
              label="IRN (Optional)"
              sx={{ width: '32.5%' }}
              InputLabelProps={{ shrink: true }}
              autoComplete="off"
              value={formik?.values?.reference}
              onChange={(e) => {
                let value = e.target.value.replace(/\*/g, '')
                const cleaned = sanitizeComment(value, 'reference')
                if (formik.values.reference !== cleaned) {
                  formik.setFieldValue('reference', cleaned)
                }
              }}
              inputProps={{ maxLength: 50 }}
              error={
                Boolean(illegalCharInReference) || Boolean(formik?.values?.reference?.includes('*'))
              }
              helperText={
                formik?.values?.reference?.includes('*')
                  ? 'Asterisks (*) are not allowed'
                  : illegalCharInReference
                  ? illegalCharacterMessage
                  : ''
              }
            />
          </Grid>
        </SectionBreak>

        {/*<SectionBreak>
          <ElementBreak>
            <Grid container sx={{ margin: ' 10px 0 0 0' }} spacing={2}>
              <Item>
                <PaymentInstructions />
              </Item>
            </Grid>
          </ElementBreak>
        </SectionBreak>*/}

        <Box>
          <Grid container spacing={2}>
            <Grid container item spacing={{ xs: 1, sm: 4 }}>
              <Grid item md={1}>
                <Button sx={{ ...commonNoActionButtonStyle }} onClick={exitCreationProcess}>
                  Exit
                </Button>
              </Grid>
              <Grid item md={2}>
                <Button
                  variant="contained"
                  type="submit"
                  data-testid="review-and-submit-btn"
                  id="review-and-submit-btn"
                  sx={{ ...commonProceedButtonStyle }}
                >
                  Review and Submit
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </form>
    </div>
  )
}

export default CommercialLease
